import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet-fullscreen";
import "leaflet-fullscreen/dist/leaflet.fullscreen.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet/dist/leaflet.css";
import "./map.css";
import ReactDOMServer from "react-dom/server";
import "leaflet-polylinedecorator";
import india from "../../../LocationDashboardv2/india.json";

const NewMapTransporterProfile = ({
  map_data = { locations: [], network: [], branch_count: 0, network_count: 0 },
}) => {
  const map1Ref = useRef(null);
  const map2Ref = useRef(null);
  const map1Instance = useRef(null);
  const map2Instance = useRef(null);
  const [activeMap, setActiveMap] = useState("map1");

  const [mappedLocations, setMappedLocations] = useState([]);
  const [laneData, setLaneData] = useState([]);

  useEffect(() => {
    // Initialize Maps Only Once
    if (!map1Instance.current && !map2Instance.current) {
      initMaps();
    }
  }, []);

  useEffect(() => {
    // Update maps when props change
    const locations = map_data.locations || [];
    const lanes = map_data.network || [];

    setMappedLocations(locations.map((item) => ({ locations: [item] })));
    setLaneData(lanes);

    setTimeout(() => {
      map1Instance.current?.invalidateSize();
      map2Instance.current?.invalidateSize();
    }, 3000);
  }, [map_data]);

  useEffect(() => {
    if (mappedLocations.length && map1Instance.current) {
      updateMarkers();
    }
  }, [mappedLocations]);

  useEffect(() => {
    if (laneData.length && map2Instance.current) {
      plotNetwork();
    }
  }, [laneData]);

  const initMaps = () => {
    if (map1Ref.current) {
      map1Instance.current = L.map(map1Ref.current, {
        center: [23.5937, 78.9629],
        zoom: 4.5,
        maxZoom: 18,
        minZoom: 2,
        zoomSnap: 0.5,
        zoomDelta: 0.5,
        zoomControl: false,
      });

      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {
          attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
          subdomains: "abcd",
          maxZoom: 19,
        },
      ).addTo(map1Instance.current);

      const latLngs = L.GeoJSON.coordsToLatLngs(
        india.features[0].geometry.coordinates,
        2,
      );
      L.polyline(latLngs, { color: "#393185", weight: 2, opacity: 0.2 }).addTo(
        map1Instance.current,
      );

      L.control
        .fullscreen({ position: "topright", forcePseudoFullscreen: true })
        .addTo(map1Instance.current);
    }

    if (map2Ref.current) {
      map2Instance.current = L.map(map2Ref.current, {
        center: [23.5937, 78.9629],
        zoom: 4.5,
        maxZoom: 6,
        minZoom: 4,
        zoomSnap: 0.5,
        zoomDelta: 0.5,
        zoomControl: false,
      });

      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {
          attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
          subdomains: "abcd",
          maxZoom: 19,
        },
      ).addTo(map2Instance.current);

      const latLngs1 = L.GeoJSON.coordsToLatLngs(
        india.features[0].geometry.coordinates,
        2,
      );
      L.polyline(latLngs1, { color: "#393185", weight: 2, opacity: 0.2 }).addTo(
        map2Instance.current,
      );

      L.control
        .fullscreen({ position: "topright" })
        .addTo(map2Instance.current);
    }
  };

  const createFeatureCollection = (locations) => {
    return locations
      .filter(
        (loc) =>
          loc.locations?.[0]?.lat !== "NA" && loc.locations?.[0]?.lon !== "NA",
      )
      .map((location, i) => ({
        type: "Feature",
        properties: {
          description: renderDescription(location),
          icon: i === 0 ? "marker" : "markerbranch",
        },
        geometry: {
          type: "Point",
          coordinates: [location.locations[0].lon, location.locations[0].lat],
        },
      }));
  };

  const renderDescription = (locationData) => {
    return ReactDOMServer.renderToStaticMarkup(
      <div>
        <h5>
          {locationData.locations[0].location},{" "}
          {locationData.locations[0].state}
        </h5>
      </div>,
    );
  };

  const updateMarkers = () => {
    const map = map1Instance.current;
    if (!map) return;

    const mcg = L.markerClusterGroup({
      iconCreateFunction: function (cluster) {
        const childCount = cluster.getChildCount();
        let c = " marker-cluster1-";
        if (childCount < 1) {
          c += "small1";
        } else if (childCount < 100) {
          c += "medium1";
        } else {
          c += "large1";
        }
        return new L.DivIcon({
          html: `<div><span>${childCount}</span></div>`,
          className: `marker-cluster1${c}`,
          iconSize: new L.Point(20, 20),
        });
      },
      chunkedLoading: true,
      singleMarkerMode: true,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 25,
      spiderLegPolylineOptions: { weight: 3, color: "red", opacity: 0.75 },
    });

    const features = createFeatureCollection(mappedLocations);
    const bounds = L.latLngBounds();
    features.forEach((feature) => {
      const coords = [
        feature.geometry.coordinates[1],
        feature.geometry.coordinates[0],
      ];
      const marker = L.marker(new L.LatLng(coords[0], coords[1]));
      bounds.extend(coords);
      marker.bindPopup(feature.properties.description);
      let isClicked = false;
      marker.on("mouseover", (ev) => {
        isClicked = false;
        ev.target.openPopup();
      });
      marker.on("mouseout", (ev) => {
        if (!isClicked) ev.target.closePopup();
      });
      marker.on("click", (ev) => {
        isClicked = true;
        ev.target.openPopup();
      });

      mcg.addLayer(marker);
    });

    map.addLayer(mcg);

    if (features.length > 0) {
      map.fitBounds(bounds, {
        padding: [100, 100],
        maxZoom: 3,
      });
    }
  };

  const plotNetwork = () => {
    const map = map2Instance.current;
    if (!map) return;

    const bounds = L.latLngBounds();

    laneData.forEach((lane) => {
      const origin = new L.latLng([lane.origin.lat, lane.origin.lon]);
      const destination = new L.latLng([
        lane.destination.lat,
        lane.destination.lon,
      ]);

      bounds.extend(origin);
      bounds.extend(destination);

      const line = new L.polyline([origin, destination], {
        color: "#007AFF",
        weight: 1,
        opacity: 0.3,
      });
      line.addTo(map);

      L.polylineDecorator(line, {
        patterns: [
          {
            endOffset: "0%",
            offset: "53%",
            repeat: "0%",
            symbol: L.Symbol.arrowHead({
              pixelSize: 6,
              polygon: false,
              pathOptions: {
                opacity: 0.3,
                weight: 2,
                stroke: true,
                color: "#007AFF",
              },
            }),
          },
        ],
      }).addTo(map);
    });

    if (laneData.length > 0) {
      map.fitBounds(bounds, {
        padding: [120, 120],
        maxZoom: 5,
      });
    }
  };

  const openCity = (cityName) => {
    setActiveMap(cityName);
    window.dispatchEvent(new Event("resize"));
    setTimeout(() => {
      if (cityName === "map1") map1Instance.current?.invalidateSize();
      if (cityName === "map2") map2Instance.current?.invalidateSize();
    }, 200);
  };

  return (
    <>
      <div className="tab">
        <button
          className={`tablinks ${activeMap === "map1" ? "active" : ""}`}
          onClick={() => openCity("map1")}
        >
          Branch List ({map_data.branch_count ?? 0})
        </button>
        <button
          className={`tablinks ${activeMap === "map2" ? "active" : ""}`}
          onClick={() => openCity("map2")}
        >
          Operating Lanes ({map_data.network_count ?? 0})
        </button>
      </div>

      <div style={{ height: "500px" }}>
        <div
          id="map1"
          ref={map1Ref}
          className="tabcontent"
          style={{ display: activeMap === "map1" ? "block" : "none" }}
        />
        <div
          id="map2"
          ref={map2Ref}
          className="tabcontent"
          style={{ display: activeMap === "map2" ? "block" : "none" }}
        />
      </div>
    </>
  );
};

export default NewMapTransporterProfile;
